/* mgs-delivery-options.js — MGS EMAX native-variant Delivery Option controller.
 *
 * Drives show/hide off the selected "Delivery Option" variant (watched via a MutationObserver on
 * <variant-selection>, since this theme does not actually emit `variant:changed`; that event and a
 * radio-change delegate are kept as fallbacks).
 *
 *   Warehouse Pickup   -> show warehouse location, enable "Warehouse Pickup Location" property,
 *                         hide freight form + acknowledgment, SHOW normal Add to Cart.
 *   Ship to My Address -> hide warehouse, disable pickup property, show freight form,
 *                         HIDE Add to Cart (+ Buy-it-now) until the customer gets a quote OR uses the
 *                         "order now / contact me" skip path. Skip reveals a CHECKED acknowledgment
 *                         checkbox ("This is a Plus Shipping Item…", the "Shipping" line-item
 *                         property) and reveals Add to Cart. Unchecking it hides Add to Cart again,
 *                         so the Ship variant can never reach checkout without the acknowledgment.
 *
 * freight_quote.js handles the green skip note + form-field hiding; this file owns the panels,
 * the acknowledgment state, the ATC gate, and resetting everything on each variant change.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__mgsDeliveryInit) return;
    root.__mgsDeliveryInit = true;

    var form        = root.closest('form[data-product-form]') || root.closest('form');
    var pickupPanel = root.querySelector('[data-mgs-delivery-pickup]');
    var shipPanel   = root.querySelector('[data-mgs-delivery-ship]');
    var pickupProp  = root.querySelector('[data-mgs-pickup-prop]');
    var ackLabel    = root.querySelector('[data-mgs-delivery-ack]');
    var shipAck     = root.querySelector('[data-mgs-ship-ack]');
    var fqRoot      = root.querySelector('[data-mgs-fq-root]');
    var atc         = form ? form.querySelector('.product-form--atc') : null;

    var pickupValue = root.getAttribute('data-pickup-value') || 'Warehouse Pickup';
    var shipValue   = root.getAttribute('data-ship-value') || 'Ship to My Address';
    var idx = parseInt(root.getAttribute('data-delivery-option-index'), 10);
    if (isNaN(idx) || idx < 0) idx = 0;
    var mode = root.getAttribute('data-mgs-delivery-mode') || 'quote';

    var map = {};
    try { var mapEl = root.querySelector('[data-mgs-delivery-map]'); if (mapEl) map = JSON.parse(mapEl.textContent || '{}'); } catch (e) {}

    function enableInput(input, on) { if (!input) return; if (on) input.removeAttribute('disabled'); else input.setAttribute('disabled', 'disabled'); }
    function setHidden(el, hidden) { if (!el) return; if (hidden) el.setAttribute('hidden', ''); else el.removeAttribute('hidden'); }
    function showAtc(show) { if (atc) atc.style.display = show ? '' : 'none'; }

    function hideAck() { if (ackLabel) ackLabel.classList.add('mgs-fq-ack-hidden'); if (shipAck) shipAck.checked = false; }
    function revealAck() { if (ackLabel) ackLabel.classList.remove('mgs-fq-ack-hidden'); if (shipAck) shipAck.checked = true; }

    // Restore the freight form to its pristine (pre-quote / pre-skip) state.
    function resetFreightForm() {
      if (!fqRoot) return;
      ['.mgs-fq-grid', '.mgs-fq-cta-row', '.mgs-fq-promise', '[data-mgs-fq-skip]'].forEach(function (sel) {
        var el = fqRoot.querySelector(sel); if (el) el.style.display = '';
      });
      var note = fqRoot.querySelector('.mgs-fq-skip-note'); if (note && note.parentNode) note.parentNode.removeChild(note);
      var c = fqRoot.querySelector('[data-mgs-fq-confirm]'); if (c) c.hidden = true;
      var er = fqRoot.querySelector('[data-mgs-fq-error]'); if (er) er.hidden = true;
    }

    function showPickup() {
      setHidden(shipPanel, true);
      setHidden(pickupPanel, false);
      enableInput(pickupProp, true);
      hideAck();
      showAtc(true);
      root.removeAttribute('data-mgs-ship-ready');
    }

    function showShip() {
      setHidden(pickupPanel, true);
      setHidden(shipPanel, false);
      enableInput(pickupProp, false);
      if (mode === 'quote') {
        hideAck();               // acknowledgment hidden + unchecked until the skip path
        showAtc(false);          // hide Add to Cart + Buy-it-now until quote/skip
        resetFreightForm();
      } else {
        // calculated: shipping is baked into the (higher) Ship variant price; free: ships free.
        // Either way Ship is a normal add — no quote form, no acknowledgment, no ATC gating.
        showAtc(true);
      }
      root.removeAttribute('data-mgs-ship-ready');
    }

    function apply(value) { if (value === shipValue) showShip(); else showPickup(); }

    function valueFromVariantId(id) { if (id == null) return null; var v = map[String(id)]; return v == null ? null : v; }
    function valueFromVariant(variant) {
      if (!variant) return null;
      if (Array.isArray(variant.options) && variant.options[idx] != null) return variant.options[idx];
      if (variant['option' + (idx + 1)] != null) return variant['option' + (idx + 1)];
      if (variant.id != null) { var byId = valueFromVariantId(variant.id); if (byId != null) return byId; }
      if (typeof variant.title === 'string') return variant.title;
      return null;
    }

    // "Order now / be contacted about shipping" skip path (Ship mode only).
    function onSkip() {
      if (!shipPanel || shipPanel.hasAttribute('hidden')) return;
      revealAck();                 // reveal + check the acknowledgment (freight_quote.js also does; idempotent)
      showAtc(true);
      root.setAttribute('data-mgs-ship-ready', '1');
      if (atc && atc.scrollIntoView) { try { atc.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }
    }
    root.addEventListener('click', function (e) { if (e.target && e.target.closest && e.target.closest('[data-mgs-fq-skip]')) onSkip(); });

    // Constraint: Ship cannot reach checkout without the acknowledgment — keep ATC gated on it.
    if (shipAck) shipAck.addEventListener('change', function () {
      if (root.getAttribute('data-mgs-ship-ready') === '1') showAtc(shipAck.checked);
    });

    // ---- variant resolution (robust across themes) ----
    var variantSelection = document.querySelector('[data-variant-selection]');
    function applyFromSelection() {
      var id = variantSelection ? variantSelection.getAttribute('variant') : null;
      if ((!id || id === 'not-selected') && form) { var idEl = form.querySelector('[name="id"]'); if (idEl) id = idEl.value; }
      var value = valueFromVariantId(id);
      if (value != null) { apply(value); return true; }
      return false;
    }
    if (variantSelection && window.MutationObserver) {
      new MutationObserver(applyFromSelection).observe(variantSelection, { attributes: true, attributeFilter: ['variant'] });
    }
    document.addEventListener('variant:changed', function (e) {
      var d = e && e.detail; var value = valueFromVariant(d && d.variant ? d.variant : d);
      if (value != null) apply(value); else applyFromSelection();
    });
    if (form) {
      form.addEventListener('change', function (e) {
        var t = e.target;
        if (t && t.matches && (t.matches('[data-variant-option-value]') || t.matches('[name="id"]'))) setTimeout(applyFromSelection, 0);
      });
    }

    if (!applyFromSelection()) apply(pickupValue);
  }

  function boot() { var roots = document.querySelectorAll('[data-mgs-delivery-root]'); for (var i = 0; i < roots.length; i++) init(roots[i]); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
